// src/pages/users/UserRolesPermissions.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Refresh as ResetIcon,
  Security as SecurityIcon,
  VpnKey as PermissionsIcon
} from '@mui/icons-material';

// Import components
import PermissionMatrix from '../../components/permissions/PermissionMatrix';
import ScopeSelector from '../../components/permissions/ScopeSelector';
import TabPanel from '../../components/ui/TabPanel';

// Import stores
import useRoleStore from '../../stores/roleStore';
import useAuthStore from '../../stores/authStore';
import permissionsService from '../../services/permissionService';

/**
 * User permissions component that integrates with role system
 * 
 * @param {Object} props
 * @param {Array} props.permissions - Current user permissions
 * @param {String} props.role - Current user role
 * @param {Function} props.onChange - Called when permissions change
 */
const UserRolesPermissions = ({ permissions = [], roleId: userRoleId, onChange }) => {
  // State
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRoleId, setSelectedRoleId] = useState(userRoleId || '');
  const [customPermissions, setCustomPermissions] = useState(permissions);
  const [useCustomPermissions, setUseCustomPermissions] = useState(permissions.length > 0);
  const [permissionsChanged, setPermissionsChanged] = useState(false);
  
  // Get current user from auth store
  const { user: currentUser, isSuperAdmin } = useAuthStore();
  
  // Get roles from role store
  const { 
    roles, 
    loading: rolesLoading, 
    error: rolesError,
    constants: permissionConstants,
    fetchRoles,
    fetchPermissionConstants
  } = useRoleStore();
  
  // Permission resources and actions
  const [resources, setResources] = useState([]);
  const [actions, setActions] = useState([]);
  const [scopes, setScopes] = useState([]);
  
  // Load roles and permission constants on mount
  useEffect(() => {
    // Fetch roles and permission constants if not already loaded
    if (roles.length === 0) {
      fetchRoles();
    }
    
    // Fetch permission constants
    fetchPermissionConstants();
  }, []);
  
  // Set up permission resources, actions and scopes when constants are loaded
  useEffect(() => {
    if (permissionConstants && permissionConstants.resources) {
      // If constants are in object format, convert to arrays
      if (typeof permissionConstants.resources === 'object' && !Array.isArray(permissionConstants.resources)) {
        setResources(Object.values(permissionConstants.resources));
        setActions(Object.values(permissionConstants.actions || {}));
        setScopes(Object.values(permissionConstants.scopes || {}));
      } else {
        // Constants are already arrays
        setResources(permissionConstants.resources || []);
        setActions(permissionConstants.actions || []);
        setScopes(permissionConstants.scopes || []);
      }
    }
  }, [permissionConstants]);
  
  // Set initial permissions
  useEffect(() => {
    if (permissions.length > 0) {
      setCustomPermissions(permissions);
      setUseCustomPermissions(true);
    }
  }, [permissions]);
  
  // Update selected role when userRoleId changes
  useEffect(() => {
    if (userRoleId) {
      setSelectedRoleId(userRoleId);
    }
  }, [userRoleId]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Handle role change
  const handleRoleChange = (event) => {
    const newRoleId = event.target.value;
    setSelectedRoleId(newRoleId);
    
    // Get role object
    const role = roles.find(r => r._id === newRoleId || r.id === newRoleId);
    
    // Apply role permissions
    if (role && role.permissions) {
      // Convert role permissions to the format needed by PermissionMatrix
      const formattedPermissions = role.permissions.map(p => ({
        resource: p.resource,
        action: p.action,
        scope: p.scope || 'all'
      }));
      
      setCustomPermissions(formattedPermissions);
      setPermissionsChanged(true);
      
      // Call onChange with new roleId and permissions
      onChange({
        roleId: newRoleId,
        permissions: formattedPermissions
      });
    }
  };
  
  // Handle permissions change from PermissionMatrix
  const handlePermissionsChange = (newPermissions) => {
    setCustomPermissions(newPermissions);
    setPermissionsChanged(true);
    
    // Call onChange with new permissions
    onChange({
      roleId: selectedRoleId,
      permissions: newPermissions
    });
  };
  
  // Handle use custom permissions toggle
  const handleUseCustomPermissions = (event) => {
    const useCustom = event.target.checked;
    setUseCustomPermissions(useCustom);
    
    if (!useCustom) {
      // Get role object
      const role = roles.find(r => r._id === selectedRoleId || r.id === selectedRoleId);
      
      // Apply role permissions
      if (role && role.permissions) {
        // Convert role permissions to the format needed by PermissionMatrix
        const formattedPermissions = role.permissions.map(p => ({
          resource: p.resource,
          action: p.action,
          scope: p.scope || 'all'
        }));
        
        setCustomPermissions(formattedPermissions);
        setPermissionsChanged(true);
        
        // Call onChange with new permissions
        onChange({
          roleId: selectedRoleId,
          permissions: formattedPermissions
        });
      } else {
        // No role found, use empty permissions
        setCustomPermissions([]);
        onChange({
          roleId: selectedRoleId,
          permissions: []
        });
      }
    }
  };
  
  // Reset permissions to defaults for the selected role
  const resetToDefaults = () => {
    // Get role object
    const role = roles.find(r => r._id === selectedRoleId || r.id === selectedRoleId);
    
    // Apply role permissions
    if (role && role.permissions) {
      // Convert role permissions to the format needed by PermissionMatrix
      const formattedPermissions = role.permissions.map(p => ({
        resource: p.resource,
        action: p.action,
        scope: p.scope || 'all'
      }));
      
      setCustomPermissions(formattedPermissions);
      setPermissionsChanged(true);
      
      // Call onChange with new permissions
      onChange({
        roleId: selectedRoleId,
        permissions: formattedPermissions
      });
    }
  };
  
  // Check if current user can edit permissions
  const canEditPermissions = () => {
    return isSuperAdmin() || currentUser?.permissions?.some(p => 
      p.resource === 'users' && p.action === 'update' && p.scope === 'all'
    );
  };
  
  // Get available roles based on current user's permissions
  const getAvailableRoles = () => {
    if (!roles.length) return [];
    
    // Super admin can assign any role
    if (isSuperAdmin()) {
      return roles;
    }
    
    // Check if user has admin permissions on users
    const hasAdminUserPermissions = currentUser?.permissions?.some(p => 
      p.resource === 'users' && p.action === 'delete' && p.scope === 'all'
    );
    
    if (hasAdminUserPermissions) {
      // Admin can assign any role except superadmin
      return roles.filter(r => r.key !== 'superadmin');
    }
    
    // Check if user has client-level user management permissions
    const hasClientUserPermissions = currentUser?.permissions?.some(p => 
      p.resource === 'users' && p.action === 'update' && p.scope === 'client'
    );
    
    if (hasClientUserPermissions) {
      // Can assign client roles
      return roles.filter(r => 
        ['client-admin', 'client-manager', 'client-user', 'user'].includes(r.key)
      );
    }
    
    // Default - very restricted
    return roles.filter(r => r.key === 'user');
  };
  
  // Group roles by category for the select
  const groupRolesByCategory = (availableRoles) => {
    return availableRoles.reduce((acc, role) => {
      const category = role.category || 'Other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(role);
      return acc;
    }, {});
  };
  
  // Format role name for display
  const formatRoleName = (roleKey) => {
    if (!roleKey) return '';
    
    // Find role in roles array
    const roleObj = roles.find(r => r.key === roleKey);
    if (roleObj) return roleObj.name;
    
    // Fallback to formatting the key
    return roleKey
      .replace(/-/g, ' ')
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  // Get roles ready for select grouping
  const availableRoles = getAvailableRoles();
  const groupedRoles = groupRolesByCategory(availableRoles);
  
  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1">
          User Permissions
        </Typography>
        
        <Button
          variant="outlined"
          size="small"
          startIcon={<ResetIcon />}
          onClick={resetToDefaults}
          disabled={!canEditPermissions() || rolesLoading}
        >
          Reset to Defaults
        </Button>
      </Box>
      
      {rolesError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {rolesError}
        </Alert>
      )}
      
      <Tabs 
        value={activeTab} 
        onChange={handleTabChange} 
        aria-label="permission tabs"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab 
          icon={<SecurityIcon />} 
          label="Role" 
          id="role-tab-0"
          aria-controls="role-tabpanel-0"
        />
        <Tab 
          icon={<PermissionsIcon />} 
          label="Permissions" 
          id="role-tab-1"
          aria-controls="role-tabpanel-1"
        />
      </Tabs>
      
      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Assign a role to determine the user's base permissions. You can customize individual permissions in the Permissions tab.
            </Alert>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={rolesLoading || !canEditPermissions()}>
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedRoleId}
                onChange={handleRoleChange}
                label="Role"
              >
                {Object.entries(groupedRoles).map(([category, categoryRoles]) => [
                  <MenuItem key={`group-${category}`} disabled>
                    <Typography variant="caption" color="textSecondary">
                      {category}
                    </Typography>
                  </MenuItem>,
                  ...categoryRoles.map((role) => (
                    <MenuItem key={role._id || role.id} value={role._id || role.id}>
                      {role.name}
                    </MenuItem>
                  ))
                ]).flat()}
              </Select>
            </FormControl>
            
            {rolesLoading && (
              <Box sx={{ display: 'flex', mt: 2 }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  Loading roles...
                </Typography>
              </Box>
            )}
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Role Description
            </Typography>
            
            {rolesLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Typography variant="body2" color="textSecondary">
                {roles.find(r => r._id === selectedRoleId || r.id === selectedRoleId)?.description || 
                 'Role with standard permissions.'}
              </Typography>
            )}
          </Grid>
        </Grid>
      </TabPanel>
      
      <TabPanel value={activeTab} index={1}>
        {canEditPermissions() ? (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={useCustomPermissions}
                  onChange={handleUseCustomPermissions}
                  disabled={!canEditPermissions()}
                />
              }
              label="Use custom permissions"
              sx={{ mb: 2 }}
            />
            
            {useCustomPermissions ? (
              <PermissionMatrix
                resources={resources}
                actions={actions}
                scopes={scopes}
                selectedPermissions={customPermissions}
                onChange={handlePermissionsChange}
                loading={rolesLoading}
                disabled={!canEditPermissions()}
                descriptions={{
                  resources: {
                    users: 'Manage system users and their permissions',
                    clients: 'Manage client organizations',
                    brands: 'Manage client brands',
                    content: 'Manage content for social media and marketing',
                    campaigns: 'Manage marketing campaigns',
                    templates: 'Manage content and campaign templates',
                    workflows: 'Manage approval and content workflows',
                    finances: 'Manage financial aspects like invoices',
                    analytics: 'Access to analytics and reporting',
                    reports: 'Generate and manage reports',
                    settings: 'Manage system and client settings',
                    approvals: 'Manage approval processes'
                  },
                  actions: {
                    create: 'Create new resources',
                    read: 'View resource details',
                    update: 'Modify existing resources',
                    delete: 'Delete resources from the system'
                  }
                }}
              />
            ) : (
              <Alert severity="info">
                Using standard permissions from the selected role. 
                Enable "Use custom permissions" to modify.
              </Alert>
            )}
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            <Alert severity="info">
              Permissions can only be edited by Administrators. The default permissions for the selected role will be applied.
            </Alert>
          </Box>
        )}
      </TabPanel>
    </Box>
  );
};

export default UserRolesPermissions;